import { createUserApi, deleteUserAsAdminApi, getAllAdminAssistantsApi } from 'Constants';
import { Context } from 'Context/Context';
import FormErrorBanner from 'Components/Element/FormErrorBanner';
import FieldError from 'Components/Element/FieldError';
import AreYouSureModal from 'Modals/AreYouSureModal';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CheckCircle2, UserPlus, Users } from 'lucide-react';

// Admin profile → "Manage users": admin-assistant accounts (list + revoke) and the
// invite form. Visual only — same APIs and payloads as before.

const fieldClass = 'block w-full rounded-md border bg-white py-2.5 px-3 text-navy text-sm font-plexsans placeholder:text-[rgba(20,33,61,0.35)] focus:outline-none focus:ring-1 transition-colors';
const labelStyle = { display: 'block', font: "600 12.5px/1 'IBM Plex Sans',sans-serif", color: '#14213d', marginBottom: '7px' };
const cardStyle = { border: '1px solid rgba(20,33,61,.12)', borderRadius: '8px', background: '#fff' };

const isEmailValid = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test((email || '').trim());

const initialsOf = (first, last, email) => {
    const a = (first || '').trim()[0] || '';
    const b = (last || '').trim()[0] || '';
    return ((a + b) || (email || '?')[0]).toUpperCase();
};

const friendlyError = (name) => {
    if (!name) return 'Something went wrong. Please try again.';
    if (name === 'UsernameExistsException') return 'An account with this email already exists.';
    if (name === 'InvalidParameterException') return 'Please check the name and email and try again.';
    return (typeof name === 'string' && !/Exception$/.test(name)) ? name : 'Something went wrong. Please try again.';
};

function AddUser() {
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserFullName, setNewUserFullName] = useState('');
    const [touched, setTouched] = useState({ name: false, email: false });
    const [error, setError] = useState('');
    const [lastAdded, setLastAdded] = useState(null);   // { name, email } after a successful invite
    const [busy, setBusy] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [existingUsers, setExistingUsers] = useState([]);
    const [updateList, setUpdateList] = useState(false);
    const [toRevoke, setToRevoke] = useState(null);
    const nameRef = useRef(null);
    const {makeApiCallFunc, userDetails} = useContext(Context);
    const navigate = useNavigate();

    useEffect(() => {
        if(userDetails.isAdminAssistant)
        {
            navigate("/admin/approve")
        }
        else
        {
        makeApiCallFunc(getAllAdminAssistantsApi,
            null,
            (data) => { setExistingUsers(data?.data || []); setLoadingList(false); },
            (data) => { console.log(data); setLoadingList(false); }
        )
        }
    }, [updateList])

    const nameError = touched.name && !newUserFullName.trim() ? 'Please enter a full name' : '';
    const emailError = touched.email && !isEmailValid(newUserEmail) ? 'Please enter a valid email address' : '';

    const revoke = () => {
        const user = toRevoke;
        if (!user) return;
        setToRevoke(null);
        makeApiCallFunc(deleteUserAsAdminApi,
            {username: user.email, id: user._id},
            () => {
                toast.success(`Access revoked for ${[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}`);
                setUpdateList(prev => !prev);
            },
            (data) => { console.log(data); toast.error('Could not revoke access. Please try again.'); }
        )
    }

    const createUserFunc = (e) => {
        e && e.preventDefault();
        setTouched({ name: true, email: true });
        if (!newUserFullName.trim() || !isEmailValid(newUserEmail) || busy) return;
        const name = newUserFullName.trim();
        const email = newUserEmail.trim();
        setBusy(true);
        setError('');
        makeApiCallFunc(createUserApi, {
            name: name,
            username: email,
        },
        (data) => {
            setBusy(false);
            // Cognito errors come back as a 200 with { name: '...Exception' }
            const errName = data?.data?.name;
            if(typeof errName === 'string' && /Exception$/.test(errName))
            {
                setError(friendlyError(errName));
                return;
            }
            // success → clear the form so the next person can be added right away
            setLastAdded({ name, email });
            setNewUserFullName('');
            setNewUserEmail('');
            setTouched({ name: false, email: false });
            toast.success(`Invite sent to ${email}`);
            setUpdateList(prev => !prev);
            nameRef.current && nameRef.current.focus();
        },
        (data) => {
            setBusy(false);
            setError(friendlyError(data?.response?.data?.name || data?.data?.name));
        }
        )
    }

    const count = existingUsers?.length || 0;

  return (
    <div className='w-full font-plexsans' style={{ maxWidth: '860px' }}>
        <AreYouSureModal
            uploadNew={!!toRevoke}
            onClose={() => setToRevoke(null)}
            buttonName='Revoke access'
            text={toRevoke ? `${[toRevoke.firstName, toRevoke.lastName].filter(Boolean).join(' ') || toRevoke.email} will no longer be able to sign in to the admin panel.` : ''}
            onSubmit={revoke}
        />

        <h2 style={{ margin: '0 0 4px', font: "400 22px/1.25 'Spectral',serif", color: '#14213d' }}>Manage users</h2>
        <p style={{ margin: '0 0 26px', font: "400 13.5px/1.5 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.55)' }}>
            Admin assistants can review and approve professionals. New members get an email invite to set their password.
        </p>

        {/* Existing admin assistants */}
        <div style={{ ...cardStyle, marginBottom: '22px', overflow: 'hidden' }}>
            <div className='flex items-center justify-between' style={{ padding: '16px 20px', borderBottom: '1px solid rgba(20,33,61,.08)' }}>
                <div className='flex items-center' style={{ gap: '8px' }}>
                    <Users size={15} strokeWidth={1.9} color='#8a6231' />
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#14213d' }}>Admin assistants</span>
                </div>
                <span style={{ font: "500 11px/1 'IBM Plex Mono',monospace", color: 'rgba(20,33,61,.5)', background: 'rgba(20,33,61,.05)', padding: '5px 9px', borderRadius: '4px' }}>
                    {count} {count === 1 ? 'member' : 'members'}
                </span>
            </div>

            {loadingList ?
                Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className='flex items-center' style={{ padding: '14px 20px', gap: '12px', borderTop: i ? '1px solid rgba(20,33,61,.07)' : 'none' }}>
                        <div className='animate-pulse rounded-full' style={{ width: '32px', height: '32px', background: 'rgba(20,33,61,.07)' }} />
                        <div className='animate-pulse rounded' style={{ height: '10px', width: '30%', background: 'rgba(20,33,61,.07)' }} />
                    </div>
                ))
            : count === 0 ?
                <div className='text-center' style={{ padding: '30px 20px', fontSize: '13px', color: 'rgba(20,33,61,.5)' }}>
                    No admin assistants yet. Add your first one below.
                </div>
            :
                existingUsers.map((user, index) => (
                    <div
                        key={user?._id || index}
                        className='flex flex-wrap lg:flex-nowrap items-center justify-between'
                        style={{ padding: '13px 20px', gap: '12px', borderTop: index ? '1px solid rgba(20,33,61,.07)' : 'none' }}
                    >
                        <div className='flex items-center min-w-0' style={{ gap: '12px' }}>
                            <div className='flex-none flex items-center justify-center rounded-full' style={{ width: '32px', height: '32px', background: '#c9a26a', color: '#14213d', font: "600 11.5px/1 'IBM Plex Sans',sans-serif" }}>
                                {initialsOf(user?.firstName, user?.lastName, user?.email)}
                            </div>
                            <div className='min-w-0'>
                                <div className='truncate' style={{ fontWeight: 500, fontSize: '13.5px', color: '#14213d' }}>
                                    {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || '—'}
                                </div>
                                <div className='truncate' style={{ fontSize: '12.5px', color: 'rgba(20,33,61,.55)' }}>{user?.email}</div>
                            </div>
                        </div>
                        <button
                            type='button'
                            onClick={() => setToRevoke(user)}
                            className='flex-none transition-colors hover:bg-[rgba(154,59,46,0.06)]'
                            style={{ font: "500 12.5px/1 'IBM Plex Sans',sans-serif", color: '#9a3b2e', border: '1px solid rgba(154,59,46,.35)', borderRadius: '4px', padding: '9px 14px' }}
                        >
                            Revoke access
                        </button>
                    </div>
                ))
            }
        </div>

        {/* Invite form */}
        <form onSubmit={createUserFunc} noValidate style={{ ...cardStyle, padding: '22px 22px 24px' }}>
            <div className='flex items-center' style={{ gap: '8px', marginBottom: '4px' }}>
                <UserPlus size={15} strokeWidth={1.9} color='#8a6231' />
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#14213d' }}>{lastAdded ? 'Add another admin assistant' : 'Add an admin assistant'}</span>
            </div>
            <div style={{ fontSize: '12.5px', lineHeight: 1.5, color: 'rgba(20,33,61,.5)', marginBottom: '18px' }}>
                They'll receive an email with a link to set their password and sign in.
            </div>

            {lastAdded &&
                <div className='flex items-start' style={{ gap: '10px', border: '1px solid rgba(58,125,79,.3)', background: 'rgba(58,125,79,.06)', borderRadius: '6px', padding: '12px 14px', marginBottom: '18px' }}>
                    <CheckCircle2 size={16} strokeWidth={2} color='#3a7d4f' className='flex-none' style={{ marginTop: '1px' }} />
                    <div style={{ fontSize: '13px', lineHeight: 1.5, color: '#14213d' }}>
                        <span style={{ fontWeight: 600 }}>{lastAdded.name}</span> was added and an invite was sent to {lastAdded.email}. You can add more people below.
                    </div>
                </div>
            }

            <FormErrorBanner>{error}</FormErrorBanner>

            <div className='grid grid-cols-1 md:grid-cols-2' style={{ gap: '16px' }}>
                <div>
                    <label htmlFor='admin-user-name' style={labelStyle}>Full name</label>
                    <input
                        id='admin-user-name'
                        ref={nameRef}
                        type='text'
                        autoComplete='off'
                        placeholder='e.g. Jordan Lee'
                        value={newUserFullName}
                        onChange={(e) => { setNewUserFullName(e.target.value); setError(''); }}
                        onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                        className={fieldClass + (nameError ? ' border-red-400 focus:border-red-500 focus:ring-red-500' : ' border-[rgba(20,33,61,0.15)] focus:border-tan focus:ring-tan')}
                        name='name'
                    />
                    <FieldError>{nameError}</FieldError>
                </div>
                <div>
                    <label htmlFor='admin-user-email' style={labelStyle}>Email</label>
                    <input
                        id='admin-user-email'
                        type='email'
                        autoComplete='off'
                        placeholder='name@company.com'
                        value={newUserEmail}
                        onChange={(e) => { setNewUserEmail(e.target.value); setError(''); }}
                        onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                        className={fieldClass + (emailError ? ' border-red-400 focus:border-red-500 focus:ring-red-500' : ' border-[rgba(20,33,61,0.15)] focus:border-tan focus:ring-tan')}
                        name='email'
                    />
                    <FieldError>{emailError}</FieldError>
                </div>
            </div>

            <div className='flex justify-end' style={{ marginTop: '22px' }}>
                <button
                    type='submit'
                    disabled={busy}
                    className={'text-ivory transition-colors ' + (busy ? 'bg-tan opacity-60 cursor-wait' : 'bg-tan hover:bg-tanDark cursor-pointer')}
                    style={{ font: "500 13px/1 'IBM Plex Sans',sans-serif", padding: '13px 26px', borderRadius: '4px' }}
                >
                    {busy ? 'Sending invite…' : 'Send invite'}
                </button>
            </div>
        </form>
    </div>
  )
}

export default AddUser
